const MOBILE_BREAKPOINT = 1280;
const SCROLL_COMPACT_HEADER_OFFSET = 50;
const RESIZE_DEBOUNCE_DELAY = 100;

function debounce<T extends (...args: any[]) => void>(fn: T, delay: number): T {
    let timeoutId: number | undefined;
    return function(this: any, ...args: any[]) {
        clearTimeout(timeoutId);
        timeoutId = window.setTimeout(() => fn.apply(this, args), delay);
    } as T;
}

function throttleRaf<T extends (...args: any[]) => void>(fn: T): T {
    let scheduled = false;
    let lastArgs: any[];

    return function(this: any, ...args: any[]) {
        lastArgs = args;
        if (!scheduled) {
            scheduled = true;
            requestAnimationFrame(() => {
                fn.apply(this, lastArgs);
                scheduled = false;
            });
        }
    } as T;
}

export class HeaderScrollHandler {
    private header: HTMLElement;
    private headerMain: HTMLElement;
    private headerNavigation: HTMLElement;
    private headerAbove: HTMLElement;
    private navToggle: HTMLElement;
    private mainNav: HTMLElement;

    private headerHeight = 0;
    private headerMainHeight = 0;
    private headerNavigationHeight = 0;
    private headerAboveHeight = 0;
    private scrollDistanceToToggleCompactHeader = 0;

    private lastScrollY = window.scrollY;
    private lastFixedPos = 0;
    private lastScrollDirection: 'up' | 'down' = 'down';

    private debouncedResize: () => void;
    private throttledScroll: () => void;

    constructor() {
        const header = document.querySelector('header.head') as HTMLElement;
        const headerMain = header?.querySelector('.header-main') as HTMLElement;
        const headerNavigation = header?.querySelector('.header-navigation') as HTMLElement;
        const headerAbove = header?.querySelector('.header-above') as HTMLElement;
        const navToggle = header?.querySelector('.head-sticky-toggle') as HTMLElement;
        const mainNav = document.querySelector('header .head-nav-lvl1') as HTMLElement;

        if (!header || !headerMain || !headerNavigation || !headerAbove || !navToggle || !mainNav) return;

        this.header = header;
        this.headerMain = headerMain;
        this.headerNavigation = headerNavigation;
        this.headerAbove = headerAbove;
        this.navToggle = navToggle;
        this.mainNav = mainNav;

        this.debouncedResize = debounce(this.handleResize.bind(this), RESIZE_DEBOUNCE_DELAY);

        if(header.classList.contains('head-minimized')) {
            this.throttledScroll = throttleRaf(this.handleScrollForMinimizedVariant.bind(this));
        } else {
            this.throttledScroll = throttleRaf(this.handleScroll.bind(this));
        }

        if(!document.querySelector("header.head-dynamic")) return;
        this.updateHeaderMeasurements();
        window.addEventListener('resize', this.debouncedResize);
        window.addEventListener('scroll', this.throttledScroll);
        this.navToggle.addEventListener('click', this.handleNavToggle);
    }

    private updateHeaderMeasurements(): void {
        this.headerHeight = this.header.offsetHeight;
        this.headerMainHeight = this.headerMain.offsetHeight;
        this.headerNavigationHeight = this.headerNavigation.offsetHeight;
        this.headerAboveHeight = this.headerAbove.offsetHeight;
        this.scrollDistanceToToggleCompactHeader = this.headerHeight + SCROLL_COMPACT_HEADER_OFFSET;
    }

    private handleResize(): void {
        this.updateHeaderMeasurements();
    }

    private handleNavToggle = (): void => {
        this.header.classList.toggle('nav-out');
        this.header.classList.remove('nav-reset');

        const isMobile = window.innerWidth < MOBILE_BREAKPOINT;
        const isCompact = this.header.classList.contains('header-compact');
        const isNavOut = this.header.classList.contains('nav-out');

        // Mobile burger navigation
        // Calculate top offset and height of burger navigation depending on header state
        if (isMobile && isNavOut) {
            const top = isCompact ? 0 : this.headerAboveHeight + this.headerMainHeight;
            const height = `calc(100vh - ${top}px)`;

            this.mainNav.style.top = `${top}px`;
            this.mainNav.style.height = height;
            this.mainNav.style.display = 'block';
            document.documentElement.style.overflow = 'hidden';

            setTimeout(() => this.mainNav.scrollTop = 0, 10);
        } else if (isMobile && !isNavOut) {
            this.mainNav.style.top = '0';
            this.mainNav.style.height = '100%';
            this.mainNav.style.display = 'none';
            document.documentElement.style.overflow = 'visible';
        }
    }

    private handleScrollForMinimizedVariant(): void {
        const currentScrollY = window.scrollY;

        // If user scrolls the minimum distance to toggle the compact header -> calculate header height, set css class and set height to prevent layout jumps
        if (currentScrollY > this.headerAboveHeight && !this.header.classList.contains('header-compact')) {
            this.header.classList.remove('nav-reset');
            this.header.style.height = `${this.headerHeight}px`;
            this.headerNavigation.style.top = `${this.headerMainHeight}px`;
            this.header.classList.add('header-compact');
        }
        // 'Reattach' header by removing classes and fixed height on header if user scrolls back up to the bottom edge of the 'above'-part of the header, which is the original position
        if (currentScrollY <= this.headerAboveHeight) {
            this.header.classList.remove('header-compact', 'nav-out', 'nav-reset');
            this.header.style.height = 'auto';
        }
        // 'Reattach' navigation by adding nav reset class if user scrolls back up to the bottom edge of the original navs position. Only if nav is hidden
        // Check if 1) User is scrolling up AND 2) ScrollY is at bottom edge of the navs original position AND 3) the navigation is hidden
        if (this.lastScrollDirection === 'up' && currentScrollY <= this.headerAboveHeight + this.headerNavigationHeight && !this.header.classList.contains('nav-out')) {
            this.header.classList.add('nav-reset');
        }

        if (this.isScrollingUpAndScrolledMinimumDistance(currentScrollY)) {
            this.lastScrollDirection = 'up';
        }
        // Register scroll direction change from up scroll to down scroll
        if (this.lastScrollDirection === 'up' && this.lastScrollY < currentScrollY && this.header.classList.contains('header-compact')) {
            this.lastScrollDirection = 'down';

            // Fixate header at current location
            this.header.classList.remove('nav-reset');
        }

        this.lastScrollY = currentScrollY;
    }

    private handleScroll(): void {
        const currentScrollY = window.scrollY;

        // If user scrolls the minimum distance to toggle the compact header -> calculate header height, set css class and set height to prevent layout jumps
        if (currentScrollY > this.scrollDistanceToToggleCompactHeader && !this.header.classList.contains('header-compact')) {
            this.header.classList.remove('nav-reset');
            this.header.classList.add('header-compact');
            this.header.style.height = `${this.headerHeight}px`;
        }

        // 'Reattach' header by removing classes and fixed height on header if user scrolls back up to the bottom edge of the 'above'-part of the header, which is the original position
        if (currentScrollY <= this.headerAboveHeight) {
            this.header.classList.remove('header-compact', 'scroll-out', 'nav-out', 'nav-reset');
            this.header.style.height = 'auto';
        }

        // 'Reattach' navigation by adding nav reset class if user scrolls back up to the bottom edge of the original navs position. Only if nav is hidden
        // Check if 1) User is scrolling up AND 2) ScrollY is at bottom edge of the navs original position AND 3) the navigation is hidden
        if (this.lastScrollDirection === 'up' && currentScrollY <= this.headerAboveHeight + this.headerNavigationHeight && !this.header.classList.contains('nav-out')) {
            this.header.classList.add('nav-reset');
        }

        if (this.isScrollingUpAndScrolledMinimumDistance(currentScrollY)) {
            this.lastScrollDirection = 'up';

            // Fixate header at top of screen so that it sticks as long as user keeps scrolling up
            this.headerMain.style.top = '0';
            this.headerNavigation.style.top = `${this.headerMainHeight}px`;

            if (this.header.classList.contains('header-compact')) {
                this.header.classList.remove('scroll-fixed');
                this.header.classList.add('scroll-out');
            }
        }

        // Register scroll direction change from up scroll to down scroll
        if (this.lastScrollDirection === 'up' && this.lastScrollY < currentScrollY && this.header.classList.contains('header-compact')) {
            this.lastScrollDirection = 'down';
            this.lastFixedPos = currentScrollY;

            // Fixate header at current location
            this.header.classList.remove('nav-reset');
            this.header.classList.add('scroll-fixed');
            this.headerMain.style.top = `${this.lastFixedPos}px`;
            this.headerNavigation.style.top = `${this.lastFixedPos + this.headerMainHeight}px`;
        }

        // Close navigation and remove header scroll transition css class when sticky header is scrolled out of viewport
        if (
            this.lastScrollDirection === 'down' &&
            this.lastFixedPos !== 0 &&
            currentScrollY - this.lastFixedPos >= this.headerMainHeight + this.headerNavigationHeight
        ) {
            this.header.classList.remove('scroll-out', 'nav-out');
        }

        this.lastScrollY = currentScrollY;
    }

    private isScrollingUpAndScrolledMinimumDistance(currentScrollY: number) {
        // Register scroll direction change from down scroll to up scroll
        const isScrollingUp = this.lastScrollDirection === 'down' && this.lastScrollY > currentScrollY;
        // Trigger direction change only after scrolling either
        // a) down until the header is out of the viewport, or
        const stickyHeaderIsOutOfViewport = currentScrollY > this.lastFixedPos + this.headerMainHeight + this.headerNavigationHeight;
        // b) up above the last fixed position so that it keeps sticking again
        const headerWasFixedAndNeedsToBeStickyAgain = currentScrollY <= this.lastFixedPos;

        return isScrollingUp && (stickyHeaderIsOutOfViewport || headerWasFixedAndNeedsToBeStickyAgain);
    }
}